/**
 * 节点素材引用字段读写工具。
 *
 * 节点 custom data 中的 `assetRefs` 可能来自新逻辑写入的 Y.Array，
 * 也可能来自历史/外部逻辑写入的普通数组；上层统一通过这里读写，避免到处判断 Yjs 类型。
 */
import * as Y from 'yjs';

export const NODE_ASSET_REFS_FIELD = 'assetRefs';

/**
 * 过滤并去重 assetId 列表。
 *
 * @param values 待清洗的任意值数组
 * @returns 只包含非空字符串的去重 assetId 数组
 */
function uniqueStringValues(values: unknown[]) {
  return Array.from(new Set(values.filter((value): value is string => (
    typeof value === 'string' && value.length > 0
  ))));
}

function stringValues(values: unknown[]) {
  return values.filter((value): value is string => (
    typeof value === 'string' && value.length > 0
  ));
}

/**
 * 从未知存储值中读取 assetRefs。
 *
 * @param value `assetRefs` 字段原始值，支持 Y.Array 和普通数组
 * @returns 规范化后的 assetId 数组
 */
export function readAssetRefsValue(value: unknown, unique: boolean = true): string[] {
  if (value instanceof Y.Array) {
    return unique ? uniqueStringValues(value.toArray()) : stringValues(value.toArray());
  }

  if (Array.isArray(value)) {
    return unique ? uniqueStringValues(value) : stringValues(value);
  }

  return [];
}

/**
 * 从节点 Y.Map 中读取 assetRefs。
 *
 * @param yNodeData 节点 custom data 的 Y.Map；为空时返回空数组
 * @returns 当前节点引用的 assetId 数组
 */
export function readNodeAssetRefs(yNodeData: Y.Map<unknown> | undefined): string[] {
  if (!yNodeData) {
    return [];
  }

  return readAssetRefsValue(yNodeData.get(NODE_ASSET_REFS_FIELD));
}

/**
 * 将 assetRefs 写回节点 Y.Map。
 *
 * @param yNodeData 节点 custom data 的 Y.Map
 * @param assetRefs 下一批 assetId；函数内部会去重并转成 Y.Array 存储
 */
export function writeNodeAssetRefs(yNodeData: Y.Map<unknown>, assetRefs: string[]): void {
  const nextRefs = uniqueStringValues(assetRefs);
  const existing = yNodeData.get(NODE_ASSET_REFS_FIELD);

  if (existing instanceof Y.Array) {
    // 保留原 Y.Array 实例，避免替换共享类型导致观察者丢失更细粒度的变更。
    existing.delete(0, existing.length);
    if (nextRefs.length > 0) {
      existing.insert(0, nextRefs);
    }
    return;
  }

  const yRefs = new Y.Array<string>();
  if (nextRefs.length > 0) {
    yRefs.insert(0, nextRefs);
  }
  yNodeData.set(NODE_ASSET_REFS_FIELD, yRefs);
}

/**
 * 给节点追加一个 assetId 引用。
 *
 * @param yNodeData 节点 custom data 的 Y.Map
 * @param assetId 要追加的画布素材 ID
 */
export function appendNodeAssetRef(yNodeData: Y.Map<unknown>, assetId: string): void {
  writeNodeAssetRefs(yNodeData, [...readNodeAssetRefs(yNodeData), assetId]);
}

/**
 * 在节点引用开头插入一个 assetId。
 *
 * @param yNodeData 节点 custom data 的 Y.Map
 * @param assetId 要插入的画布素材 ID
 */
export function prependNodeAssetRef(yNodeData: Y.Map<unknown>, assetId: string): void {
  writeNodeAssetRefs(yNodeData, [assetId, ...readNodeAssetRefs(yNodeData)]);
}

/**
 * 从节点引用中移除一个 assetId。
 *
 * @param yNodeData 节点 custom data 的 Y.Map
 * @param assetId 要移除的画布素材 ID
 */
export function removeNodeAssetRef(yNodeData: Y.Map<unknown>, assetId: string): void {
  const refs = readNodeAssetRefs(yNodeData);
  const idx = refs.indexOf(assetId);
  if (idx !== -1) {
    const newRefs = [...refs];
    newRefs.splice(idx, 1);
    writeNodeAssetRefs(yNodeData, newRefs);
  }
}
